import { useState, type FC } from "react";
import Subtitle from "../../atoms/typography/Subtitle";
import { theme } from "../../../theme";
import { HorizontalSpacer, VerticalSpacer } from "../../atoms/Spacers";
import BodySmall from "../../atoms/typography/BodySmall";
import type { ManualRiskScore } from "../../../models/RiskScoreModel";
import { Col, Row } from "react-grid-system";
import TextField from "../../atoms/inputs/TextField";
import { useCreateRiskScoreAdjustment } from "../../../graphql/hooks/aml/useCreateRiskScoreAdjustment";
import { useDeleteRiskScoreAdjustment } from "../../../graphql/hooks/aml/useDeleteRiskScoreAdjustment";
import DatePicker from "../filters/DatePicker";
import Checkbox from "../../atoms/inputs/Checkbox";
import MultilineTextField from "../../atoms/inputs/MultilineTextField";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import ActionButton from "../../atoms/buttons/ActionButton";
import styled from "styled-components";
import Body from "../../atoms/typography/Body";
import dayjs from "dayjs";
import Small from "../../atoms/typography/Small";
import { TrashIcon } from "../../atoms/icons/TrashIcon";
import Alert from "../Alert";
import { useTranslation } from "react-i18next";
import DefaultButton from "../../atoms/buttons/DefaultButton";

interface Props {
  clientIdentifier: string;
  riskScore: number;
  manualRiskScore?: ManualRiskScore;
  currentManualRiskScore?: ManualRiskScore;
  onClose: () => void;
}

const OutlinedDiv = styled.div`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey05};
`;

const EditRiskScoreModalContent: FC<Props> = ({
  clientIdentifier,
  riskScore,
  manualRiskScore,
  currentManualRiskScore,
  onClose,
}) => {
  const { t } = useTranslation();
  const [difference, setDifference] = useState<number>(0);
  const [comment, setComment] = useState<string>("");
  const [checked, setChecked] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const onCheckValidUntilFurther = () => {
    if (checked) {
      setChecked(false);
    } else {
      setChecked(true);
      setSelectedDate(undefined);
    }
  };

  const onSelectDate = (date: Date | undefined) => {
    setSelectedDate(date);
    setChecked(false);
  };

  const onCompletedCreateAdjustment = (result?: any) => {
    if (result?.aml?.createRiskScoreAdjustment?.success) {
      setComment("");
      setSelectedDate(undefined);
      setChecked(true);
      setSuccessMessage(t("aml.editsHaveBeenSaved"));
    }
  };
  const onErrorCompleteAdjustment = (e?: any) => {
    setErrorMessage(t("aml.unableToSaveEdits" + ": " + e?.toString()));
  };
  const { createRiskScoreAdjustment, error, loading } =
    useCreateRiskScoreAdjustment({
      onCompleted: onCompletedCreateAdjustment,
      onError: onErrorCompleteAdjustment,
    });

  const onClickSave = async () => {
    try {
      await createRiskScoreAdjustment({
        variables: {
          input: {
            clientIdentifier: clientIdentifier,
            comment: comment,
            scoreModifier: difference,
            validTo: checked ? null : selectedDate,
          },
        },
      });
    } catch (error) {
      console.log(error);
      setErrorMessage(t("aml.unableToSaveEdits"));
    }
  };

  const onCompletedDeleteAdjustment = (result?: any) => {
    if (result?.aml?.deleteRiskScoreAdjustment?.success) {
      setSuccessMessage(t("aml.editsHaveBeenSaved"));
      setErrorMessage("");
      setComment("");
    } else {
      setErrorMessage(t("aml.unableToSaveEdits"));
    }
  };
  const onErrorDeleteAdjustment = (e?: any) => {
    setErrorMessage(t("aml.unableToSaveEdits" + ": " + e?.toString()));
  };
  const {
    deleteRiskScoreAdjustment,
    error: deleteError,
    loading: deleteLoading,
  } = useDeleteRiskScoreAdjustment({
    onCompleted: onCompletedDeleteAdjustment,
    onError: onErrorDeleteAdjustment,
  });

  const onClickDelete = async () => {
    try {
      await deleteRiskScoreAdjustment({
        variables: {
          input: {
            clientIdentifier: clientIdentifier,
            id: currentManualRiskScore?.id,
          },
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div>
      <Subtitle color={theme.colors.primary} textalign="center">
        {t("aml.editRiskScore")}
      </Subtitle>
      <HorizontalSpacer spacing={2} />
      <Row>
        <Col lg={6}>
          <BodySmall>
            {t("aml.actualScore")}:{" "}
            {riskScore - (manualRiskScore?.scoreModifier ?? 0)}
          </BodySmall>
          <HorizontalSpacer spacing={1} />
          <BodySmall color={theme.colors.textMuted}>
            {t("aml.editRiskScoreDescription1")}
            <HorizontalSpacer spacing={0.5} />
            {t("aml.editRiskScoreDescription2")}
            <HorizontalSpacer spacing={0.5} />
            {t("aml.editRiskScoreDescription3")}
          </BodySmall>
        </Col>

        <Col lg={6}>
          <Row nogutter justify="between" align="end">
            <TextField
              label={t("aml.difference")}
              placeholder="10"
              onChange={(value) => setDifference(+value)}
            />
            <DatePicker
              title={t("aml.editIsValidUntil")}
              onChange={(d) => onSelectDate(d)}
              value={selectedDate}
              disableDatesBeforeToday
            />
            <Checkbox
              title={t("aml.validUntilFurtherNotice")}
              checked={checked}
              onChange={onCheckValidUntilFurther}
            />
          </Row>
          <HorizontalSpacer spacing={1} />
          <MultilineTextField
            placeholder={t("common.comment")}
            onChange={(value) => setComment(value)}
            value={comment}
          />

          <HorizontalSpacer spacing={0.5} />
          <Row nogutter justify="end">
            <DefaultButton
              title={t("common.cancel")}
              icon={<CrossIcon color={theme.colors.text} />}
              onClick={onClose}
            />
            <VerticalSpacer spacing={1.5} />

            <ActionButton
              title={t("common.save")}
              color={theme.colors.primary}
              disabled={comment === undefined || comment.length < 3}
              onClick={onClickSave}
              loading={loading}
            />
          </Row>
        </Col>
      </Row>

      <HorizontalSpacer spacing={2} />
      {currentManualRiskScore && (
        <OutlinedDiv>
          <Body>{t("aml.currentChange")}</Body>
          <HorizontalSpacer spacing={1} />
          <div style={{ width: 400 }}>
            <Row justify="between" align="center" nogutter>
              <BodySmall>{t("aml.edit")}</BodySmall>
              <BodySmall fontweight={600}>
                {currentManualRiskScore.scoreModifier}
              </BodySmall>
            </Row>{" "}
            <HorizontalSpacer spacing={0.5} />
            <Row justify="between" align="center" nogutter>
              <BodySmall>{t("aml.validUntil")}</BodySmall>
              <BodySmall fontweight={600}>
                {dayjs(currentManualRiskScore.validTo).format("YYYY-MM-DD")}
              </BodySmall>
            </Row>
            <HorizontalSpacer spacing={0.5} />
            <Row justify="between" align="center" nogutter>
              <BodySmall>{t("aml.changedBy")}</BodySmall>
              <BodySmall fontweight={600}>
                {currentManualRiskScore.createdByUser?.firstName}{" "}
                {currentManualRiskScore.createdByUser?.lastName}
              </BodySmall>
            </Row>
          </div>
          <HorizontalSpacer spacing={1} />
          <BodySmall>{currentManualRiskScore?.comment?.content}</BodySmall>

          <HorizontalSpacer spacing={0.5} />
          <Small color={theme.colors.textSecondary}>
            {t("aml.currentChangeIsAppliedByNextRun")}
          </Small>
          <HorizontalSpacer spacing={1} />

          <OutlinedButton
            title={t("aml.deleteChange")}
            icon={<TrashIcon color={theme.colors.error} />}
            color={theme.colors.error}
            onClick={onClickDelete}
            loading={deleteLoading}
          />
        </OutlinedDiv>
      )}

      {(errorMessage || error || deleteError) && (
        <Alert
          type="error"
          title={t("errors.somethingWentWrong")}
          message={errorMessage ?? error?.toString() ?? deleteError?.toString()}
        />
      )}
      {successMessage && <Alert type="success" title={successMessage} />}
    </div>
  );
};

export default EditRiskScoreModalContent;
